import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/** Same CSS grid row: `offsetTop` can differ by ~1px (subpixel / zoom). */
const IRIS_SEARCH_HAIRLINE_ROW_Y_TOL = 1;

function sameIrisSearchHairlineRowY(a, b) {
  return Math.abs(a - b) <= IRIS_SEARCH_HAIRLINE_ROW_Y_TOL;
}

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  /** @type {ResizeObserver | undefined} */
  #irisSearchHairlineResizeObserver;

  /** @type {MutationObserver | undefined} */
  #irisSearchHairlineMutationObserver;

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');

    if (this.#isIrisSearchHairlineGrid()) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => this.#setupIrisSearchHairlineCellMetadata());
      });
    }
  }

  disconnectedCallback() {
    this.#teardownIrisSearchHairlineCellMetadata();
    super.disconnectedCallback();
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  #isIrisSearchHairlineGrid() {
    return (
      this.classList.contains('iris-search-results') &&
      this.classList.contains('iris-search-results--cell-borders-hairline')
    );
  }

  /**
   * Hairline borders use L+T insets on every cell; R+B only on row end / last row (shared edges).
   */
  #setupIrisSearchHairlineCellMetadata() {
    const { grid } = this.refs;
    if (!(grid instanceof HTMLElement) || !grid.classList.contains('product-grid--grid')) return;

    this.#syncIrisSearchHairlineCellMetadata();

    if (!this.#irisSearchHairlineResizeObserver) {
      this.#irisSearchHairlineResizeObserver = new ResizeObserver(() => this.#syncIrisSearchHairlineCellMetadata());
      this.#irisSearchHairlineResizeObserver.observe(grid);
    }

    if (!this.#irisSearchHairlineMutationObserver) {
      this.#irisSearchHairlineMutationObserver = new MutationObserver(() => this.#syncIrisSearchHairlineCellMetadata());
      this.#irisSearchHairlineMutationObserver.observe(grid, { childList: true });
    }
  }

  #teardownIrisSearchHairlineCellMetadata() {
    this.#irisSearchHairlineResizeObserver?.disconnect();
    this.#irisSearchHairlineResizeObserver = undefined;
    this.#irisSearchHairlineMutationObserver?.disconnect();
    this.#irisSearchHairlineMutationObserver = undefined;
  }

  #syncIrisSearchHairlineCellMetadata = () => {
    const { grid } = this.refs;
    if (!(grid instanceof HTMLElement) || !grid.classList.contains('product-grid--grid')) return;

    const items = grid.querySelectorAll(':scope > .product-grid__item');
    for (const li of items) {
      li.classList.remove(
        'iris-search-grid-cell--row-end',
        'iris-search-grid-cell--last-row',
        'iris-search-grid-cell--col-end'
      );
    }

    if (!this.#isIrisSearchHairlineGrid()) return;

    const list = Array.from(items);
    if (list.length === 0) return;

    /**
     * Per-column max offsetTop. A cell is the bottom of its column when its
     * offsetTop is the max (within tolerance) of all cells sharing its offsetLeft.
     * This covers the partial-last-row case where the row above has cells in
     * columns that are empty in the real last row (no neighbor below to paint
     * the inset top seam).
     * @type {Array<{ x: number, maxY: number }>}
     */
    const columnBuckets = [];
    const columnBucketFor = (x) => columnBuckets.find((b) => Math.abs(b.x - x) <= IRIS_SEARCH_HAIRLINE_ROW_Y_TOL);

    let maxY = -Infinity;
    for (const li of list) {
      maxY = Math.max(maxY, li.offsetTop);

      const x = li.offsetLeft;
      const bucket = columnBucketFor(x);
      if (bucket) {
        if (li.offsetTop > bucket.maxY) bucket.maxY = li.offsetTop;
      } else {
        columnBuckets.push({ x, maxY: li.offsetTop });
      }
    }

    for (let i = 0; i < list.length; i++) {
      const li = list[i];
      const next = list[i + 1];
      const y = li.offsetTop;
      const rowEnd = !next || !sameIrisSearchHairlineRowY(y, next.offsetTop);
      if (rowEnd) li.classList.add('iris-search-grid-cell--row-end');
      if (sameIrisSearchHairlineRowY(y, maxY)) li.classList.add('iris-search-grid-cell--last-row');
      const bucket = columnBucketFor(li.offsetLeft);
      if (bucket && sameIrisSearchHairlineRowY(y, bucket.maxY)) {
        li.classList.add('iris-search-grid-cell--col-end');
      }
    }
  };

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
    if (this.#isIrisSearchHairlineGrid()) {
      requestAnimationFrame(() => this.#syncIrisSearchHairlineCellMetadata());
    }
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
